import { useTranslation } from "next-i18next";

import { MerchantType } from "@/Apollo/schema";
import { cn } from "@/common/utils/css.utils";
import { ProBuyerLabel } from "@/features/pro-buyer-application/components/ProBuyerAccountLabel";
import { useProBuyerStatus } from "@/features/pro-buyer-application/hooks/useProBuyerStatus";
import useMerchant from "@/hooks/useMerchant";
import { InfoLabel } from "@/ui/cva/InfoLabel";

/** MDI domain, 16x16, the business mark from the WHO-5954 design. */
const DomainIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-4 w-4 shrink-0"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18,15H16V17H18M18,11H16V13H18M20,19H12V17H14V15H12V13H14V11H12V9H20M10,7H8V5H10M10,11H8V9H10M10,15H8V13H10M10,19H8V17H10M6,7H4V5H6M6,11H4V9H6M6,15H4V13H6M6,19H4V17H6M12,7V3H2V21H22V7H12Z" />
  </svg>
);

type AccountLabelsProps = {
  size: "small" | "medium";
  /** Profile card only: explanation line shown under the Pro Buyer label. */
  proBuyerHelper?: string;
  className?: string;
};

/**
 * WHO-5948 + WHO-5954. Account labels shown to the account owner in Mijn
 * account: green "Pro Buyer" (untranslated product name) and grey "Zakelijk
 * account" for a Business merchant. Pro Buyer always first, 8px apart,
 * wrapping onto a new line when they do not fit. Renders nothing (no empty
 * space) while loading or when neither applies. Not clickable.
 */
export const AccountLabels = ({
  size,
  proBuyerHelper,
  className,
}: AccountLabelsProps) => {
  const { t } = useTranslation();
  const status = useProBuyerStatus();
  const { merchant } = useMerchant();

  const isProBuyer = status.state === "ready" && status.proBuyer;
  const isBusiness = merchant?.type === MerchantType.Business;

  if (!isProBuyer && !isBusiness) {
    return null;
  }

  const proBuyerLabel = isProBuyer && <ProBuyerLabel size={size} />;
  const businessLabel = isBusiness && (
    <InfoLabel
      color="grey"
      variant="light"
      size={size}
      corners="square"
      leftIcon={<DomainIcon />}
      data-testid="business-account-label"
    >
      {t("account:account-label-business", "Business account")}
    </InfoLabel>
  );

  // WHO-5954 F1: on the profile card the explanation belongs to Pro Buyer,
  // so the order is Pro Buyer, explanation, Zakelijk account.
  if (isProBuyer && proBuyerHelper) {
    return (
      <span
        className={cn("flex flex-col items-start gap-2", className)}
        data-testid="account-labels"
      >
        <span className="flex flex-col items-start gap-1">
          {proBuyerLabel}
          <span className="text-sm text-grey-700">{proBuyerHelper}</span>
        </span>
        {businessLabel}
      </span>
    );
  }

  return (
    <span
      className={cn("flex flex-wrap gap-2", className)}
      data-testid="account-labels"
    >
      {proBuyerLabel}
      {businessLabel}
    </span>
  );
};
